"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import type { OnboardingStateResponse, ProfileResponse } from "@bitebalance/contracts"
import { useAmyDataRefresh } from "@/lib/useAmyDataRefresh"
import { useApiClient } from "@/api/useApiClient"
import { ApiError } from "@/api/client"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/onboarding/Checkbox"
import { MoreSubpageHeader } from "@/components/layout/MoreSubpageHeader"
import { LoadingState, ErrorState } from "@/components/state"
import { OptionGroup } from "@/components/onboarding/OptionGroup"
import { Button } from "@/components/ui/button"
import { PlanSyncBanner, usePlanSync } from "@/components/plan/PlanSyncBanner"
import { NutritionSources } from "@/components/goals/NutritionSources"
import { formatKcal, useI18n } from "@/i18n"

/**
 * /app/more/goals (2026-08 profile batch) — the user's goal set from
 * onboarding in one place: goal type (editable via the existing
 * PATCH /v1/profile flow), the computed daily calorie target with its
 * tolerance band, macro targets, body/target-weight data, time budget and
 * dietary style.
 *
 * Truthfulness rules:
 *  - the calorie target is labeled by provenance (Mifflin-St Jeor computed
 *    vs. manual) straight from calorieTarget.source — never presented as a
 *    promise;
 *  - body data comes from the stored onboarding answers; "prefer not to
 *    answer" renders as "not provided", never a fabricated number;
 *  - dietary style is a HARD constraint — this page links to the dedicated
 *    ConstraintsPage confirmation flow instead of duplicating it;
 *  - a saved goal change AUTO-regenerates the plan with an announcing
 *    banner (ADR-033, owner incident 2026-08-28) — the rebuild is stated,
 *    never silent, and the old→new calorie target is shown when it moved.
 */

interface Step4Answer {
  weightKg?: number | null
  targetWeightKg?: number | null
}
interface Step5Answer {
  dietaryStyle?: "omnivore" | "pescatarian" | "vegetarian" | "vegan"
}
interface Step9Answer {
  timePerMealMinutes?: number
  timePerDayMinutes?: number
}

function answerForStep<T>(onboarding: OnboardingStateResponse | null, step: number): T | null {
  const found = onboarding?.answers.find(a => a.step === step)
  return (found?.answer as T | undefined) ?? null
}

export default function GoalsPage() {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [onboarding, setOnboarding] = useState<OnboardingStateResponse | null>(null)
  const [state, setState] = useState<"loading" | "ready" | "error">("loading")
  const [goalDraft, setGoalDraft] = useState<ProfileResponse["goalType"] | null>(null)
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const { state: planSync, start: startPlanSync } = usePlanSync()
  // Manual calorie target (owner request 2026-08-30): the user's own number,
  // honestly handled — server-enforced safety floor (breastfeeding-aware,
  // ADR-030) with an explicit responsibility acknowledgment when below it.
  const [targetEditing, setTargetEditing] = useState(false)
  const [targetText, setTargetText] = useState("")
  const [floorAckNeeded, setFloorAckNeeded] = useState(false)
  const [floorAck, setFloorAck] = useState(false)
  /** Server-issued single-use token from the TARGET_BELOW_FLOOR 422 (ADR-036). */
  const [floorAckToken, setFloorAckToken] = useState<string | null>(null)
  const [targetState, setTargetState] = useState<"idle" | "saving" | "error">("idle")

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.getProfile(),
      // Body data/time budget/dietary style live in the stored onboarding
      // answers; a failure here degrades those sections to "not provided"
      // instead of blocking the whole page.
      api.getOnboarding().catch(() => null),
    ])
      .then(([p, o]) => {
        if (cancelled) return
        setProfile(p)
        setOnboarding(o)
        setGoalDraft(p.goalType)
        setState("ready")
      })
      .catch(() => {
        if (!cancelled) setState("error")
      })
    return () => {
      cancelled = true
    }
  }, [api])

  // ADR-036 finding 6: a target Amy set from chat shows here without a
  // reload. Drafts the user is mid-editing are deliberately left alone.
  useAmyDataRefresh(() => {
    Promise.all([api.getProfile(), api.getOnboarding().catch(() => null)])
      .then(([p, o]) => {
        setProfile(p)
        setOnboarding(o)
      })
      .catch(() => {
        /* quiet refresh keeps the last good view */
      })
  })

  async function handleSaveGoal() {
    if (!profile || goalDraft === null || goalDraft === profile.goalType) return
    setSaveState("saving")
    const prevKcal = profile.calorieTarget.kcal
    try {
      const updated = await api.patchProfile({ goalType: goalDraft })
      setProfile(updated)
      setGoalDraft(updated.goalType)
      setSaveState("saved")
      // ADR-033: the goal shapes every generated meal — rebuild now,
      // announced. The banner also states the target move when the
      // recompute changed it.
      startPlanSync({ fromKcal: prevKcal, toKcal: updated.calorieTarget.kcal })
    } catch {
      setSaveState("error")
    }
  }

  async function saveManualTarget() {
    if (!profile) return
    const kcal = Math.round(Number(targetText))
    if (!Number.isFinite(kcal) || kcal <= 0) return
    setTargetState("saving")
    const prevKcal = profile.calorieTarget.kcal
    try {
      const acknowledged = floorAckNeeded && floorAck
      const updated = await api.patchProfile({
        calorieTarget: {
          kcal,
          source: "manual",
          floorAcknowledged: acknowledged,
          // ADR-036 finding 3: the 422 hands out a single-use server token;
          // echoing it is the structural proof the disclaimer was shown.
          ...(acknowledged && floorAckToken ? { floorAckToken } : {}),
        },
      })
      setProfile(updated)
      setTargetEditing(false)
      setFloorAckNeeded(false)
      setFloorAck(false)
      setFloorAckToken(null)
      setTargetState("idle")
      startPlanSync({ fromKcal: prevKcal, toKcal: updated.calorieTarget.kcal })
    } catch (err) {
      // Below the safety floor: reveal the disclaimer + acknowledgment and
      // let the user decide — never silently retried, never a dead end.
      if (err instanceof ApiError && err.envelope.code === "TARGET_BELOW_FLOOR") {
        const details = err.envelope.details as { ackToken?: string } | undefined
        setFloorAckToken(details?.ackToken ?? null)
        setFloorAckNeeded(true)
        setTargetState("idle")
        return
      }
      setTargetState("error")
    }
  }

  async function restoreCalculatedTarget() {
    if (!profile) return
    setTargetState("saving")
    const prevKcal = profile.calorieTarget.kcal
    try {
      const updated = await api.patchProfile({
        calorieTarget: {
          kcal: profile.calorieTarget.kcal,
          source: "calculated",
          floorAcknowledged: false,
        },
      })
      setProfile(updated)
      setTargetState("idle")
      startPlanSync({ fromKcal: prevKcal, toKcal: updated.calorieTarget.kcal })
    } catch {
      setTargetState("error")
    }
  }

  if (state === "loading") return <LoadingState />
  if (state === "error" || !profile) return <ErrorState onRetry={() => window.location.reload()} />

  const step4 = answerForStep<Step4Answer>(onboarding, 4)
  const step5 = answerForStep<Step5Answer>(onboarding, 5)
  const step9 = answerForStep<Step9Answer>(onboarding, 9)
  const tolerance = profile.macroTargets.toleranceCaloriesPercent

  const dietaryStyleLabel =
    step5?.dietaryStyle !== undefined ? t(`onboarding.step5.${step5.dietaryStyle}`) : null

  return (
    <section
      aria-labelledby="goals-heading"
      className="mx-auto flex w-full max-w-2xl flex-col gap-6"
    >
      <MoreSubpageHeader title={t("moreProfile.goals.title")} headingId="goals-heading" />

      {/* ------------------------------------------------------------- Goal */}
      <div className="flex flex-col gap-4 rounded-bb-md border border-line bg-paper p-5">
        <h2 className="text-lg font-semibold text-ink">{t("moreProfile.goals.goalLabel")}</h2>
        <OptionGroup
          legend={t("moreProfile.goals.goalLabel")}
          columns={2}
          value={goalDraft}
          onChange={v => setGoalDraft(v as ProfileResponse["goalType"])}
          options={[
            { value: "maintain", label: t("onboarding.step3.maintain") },
            { value: "loss", label: t("onboarding.step3.loss") },
            { value: "gain", label: t("onboarding.step3.gain") },
            { value: "improve_health", label: t("onboarding.step3.improve") },
          ]}
        />
        <div className="flex items-center gap-3">
          <Button
            type="button"
            className="min-h-11"
            onClick={() => void handleSaveGoal()}
            disabled={saveState === "saving" || goalDraft === profile.goalType}
          >
            {saveState === "saving"
              ? t("onboarding.shell.saving")
              : t("moreProfile.goals.saveButton")}
          </Button>
          {saveState === "saved" && (
            <span className="text-sm text-green">{t("moreProfile.goals.saved")}</span>
          )}
          {saveState === "error" && (
            <span className="text-sm text-destructive">{t("moreProfile.goals.saveError")}</span>
          )}
        </div>
      </div>

      <PlanSyncBanner
        state={planSync}
        onRetry={() => startPlanSync({ fromKcal: null, toKcal: profile.calorieTarget.kcal })}
      />

      {/* --------------------------------------------------- Calorie target */}
      <div className="flex flex-col gap-2 rounded-bb-md border border-line bg-paper p-5">
        <h2 className="text-lg font-semibold text-ink">{t("moreProfile.goals.targetTitle")}</h2>
        <p className="text-2xl font-bold text-ink" data-testid="goals-kcal">
          {formatKcal(profile.calorieTarget.kcal, locale)}
        </p>
        <p className="text-sm text-ink-2">
          {profile.calorieTarget.source === "calculated"
            ? t("moreProfile.goals.computedNote")
            : t("moreProfile.goals.manualNote")}
        </p>
        <p className="text-sm text-ink-2">
          {t("moreProfile.goals.toleranceLabel", { percent: String(tolerance) })}
        </p>
        {/* App Review 1.4.1 (2026-10-24): the published sources behind this number, one click from it. */}
        <a href="#sources" className="text-sm font-medium text-ink underline">
          {t("moreProfile.goals.sources.linkLabel")}
        </a>

        {!targetEditing ? (
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => {
                setTargetText(String(profile.calorieTarget.kcal))
                setFloorAckNeeded(false)
                setFloorAck(false)
                setTargetEditing(true)
              }}
            >
              {t("moreProfile.goals.ownTargetCta")}
            </Button>
            {profile.calorieTarget.source === "manual" && (
              <Button
                type="button"
                variant="ghost"
                className="min-h-11"
                disabled={targetState === "saving"}
                onClick={() => void restoreCalculatedTarget()}
              >
                {t("moreProfile.goals.ownTargetRestore")}
              </Button>
            )}
          </div>
        ) : (
          <div className="mt-2 flex flex-col gap-3 rounded-bb-md border border-line bg-cream p-4">
            <p className="text-sm text-ink-2">{t("moreProfile.goals.ownTargetBody")}</p>
            <label className="text-sm font-medium text-ink" htmlFor="own-target-kcal">
              {t("moreProfile.goals.ownTargetLabel")}
            </label>
            <Input
              id="own-target-kcal"
              type="number"
              min={800}
              max={6000}
              value={targetText}
              onChange={e => {
                setTargetText(e.target.value)
                setFloorAckNeeded(false)
                setFloorAck(false)
              }}
              className="max-w-40"
            />
            {floorAckNeeded && (
              <div
                role="alert"
                className="flex flex-col gap-2 rounded-bb-md border border-coral/50 bg-coral/10 p-3"
              >
                <p className="text-sm font-semibold text-ink">
                  {t("moreProfile.goals.floorDisclaimerTitle")}
                </p>
                <p className="text-sm text-ink-2">{t("moreProfile.goals.floorDisclaimerBody")}</p>
                <label className="flex items-start gap-2 text-sm text-ink">
                  <Checkbox
                    id="floor-ack"
                    checked={floorAck}
                    onCheckedChange={setFloorAck}
                    className="mt-0.5"
                  />
                  {t("moreProfile.goals.floorDisclaimerAck")}
                </label>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                className="min-h-11"
                disabled={
                  targetState === "saving" ||
                  targetText.trim() === "" ||
                  (floorAckNeeded && !floorAck)
                }
                onClick={() => void saveManualTarget()}
              >
                {targetState === "saving"
                  ? t("onboarding.shell.saving")
                  : t("moreProfile.goals.ownTargetSave")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="min-h-11"
                onClick={() => setTargetEditing(false)}
              >
                {t("moreProfile.goals.ownTargetCancel")}
              </Button>
              {targetState === "error" && (
                <span className="text-sm text-destructive">
                  {t("moreProfile.goals.ownTargetError")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------ Macros */}
      <div className="flex flex-col gap-2 rounded-bb-md border border-line bg-paper p-5">
        <h2 className="text-lg font-semibold text-ink">{t("moreProfile.goals.macrosTitle")}</h2>
        <p className="text-sm text-ink-2">{t("moreProfile.goals.macrosHint")}</p>
        <a href="#sources" className="text-sm font-medium text-ink underline">
          {t("moreProfile.goals.sources.linkLabel")}
        </a>
        <dl className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <dt className="text-ink-2">{t("moreProfile.goals.proteinLabel")}</dt>
            <dd className="font-semibold text-ink">{profile.macroTargets.proteinG} g</dd>
          </div>
          <div>
            <dt className="text-ink-2">{t("moreProfile.goals.carbsLabel")}</dt>
            <dd className="font-semibold text-ink">{profile.macroTargets.carbsG} g</dd>
          </div>
          <div>
            <dt className="text-ink-2">{t("moreProfile.goals.fatLabel")}</dt>
            <dd className="font-semibold text-ink">{profile.macroTargets.fatG} g</dd>
          </div>
        </dl>
      </div>

      {/* App Review 1.4.1 (2026-10-24): the published sources behind the target, macros and minimums (#sources). */}
      <NutritionSources />

      {/* --------------------------------------------------------- Body data */}
      <div className="flex flex-col gap-2 rounded-bb-md border border-line bg-paper p-5">
        <h2 className="text-lg font-semibold text-ink">{t("moreProfile.goals.bodyTitle")}</h2>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <dt className="text-ink-2">{t("moreProfile.goals.currentWeightLabel")}</dt>
            <dd className="font-semibold text-ink">
              {step4?.weightKg != null
                ? `${step4.weightKg} kg`
                : t("moreProfile.goals.notProvided")}
            </dd>
          </div>
          <div>
            <dt className="text-ink-2">{t("moreProfile.goals.targetWeightLabel")}</dt>
            <dd className="font-semibold text-ink">
              {step4?.targetWeightKg != null
                ? `${step4.targetWeightKg} kg`
                : t("moreProfile.goals.notProvided")}
            </dd>
          </div>
        </dl>
        <Link to="/app/more/profile" className="text-xs text-ink-2 underline">
          {t("moreProfile.goals.bodyEditHint")}
        </Link>
      </div>

      {/* ------------------------------------------------------- Time budget */}
      <div className="flex flex-col gap-2 rounded-bb-md border border-line bg-paper p-5">
        <h2 className="text-lg font-semibold text-ink">{t("moreProfile.goals.timeBudgetTitle")}</h2>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <dt className="text-ink-2">{t("moreProfile.goals.perMealLabel")}</dt>
            <dd className="font-semibold text-ink">
              {step9?.timePerMealMinutes != null
                ? `${step9.timePerMealMinutes} min`
                : t("moreProfile.goals.notProvided")}
            </dd>
          </div>
          <div>
            <dt className="text-ink-2">{t("moreProfile.goals.perDayLabel")}</dt>
            <dd className="font-semibold text-ink">
              {step9?.timePerDayMinutes != null
                ? `${step9.timePerDayMinutes} min`
                : t("moreProfile.goals.notProvided")}
            </dd>
          </div>
        </dl>
      </div>

      {/* ---------------------------------------------------- Dietary style */}
      <div className="flex flex-col gap-2 rounded-bb-md border border-line bg-paper p-5">
        <h2 className="text-lg font-semibold text-ink">
          {t("moreProfile.goals.dietaryStyleTitle")}
        </h2>
        {dietaryStyleLabel && <p className="font-semibold text-ink">{dietaryStyleLabel}</p>}
        <Link to="/app/more/preferences" className="text-sm text-ink-2 underline">
          {t("moreProfile.goals.dietaryStyleEditLink")}
        </Link>
      </div>
    </section>
  )
}
